import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { Copy, Loader2 } from "lucide-react";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import Button from "../../ui/Button";
import ClickableUserName from "../../../components/shared/ClickableUserName";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  fetchPublicContractByIdAsync,
  resetPublicContractDetail,
} from "../../../redux/features/publicContractSlice";
import { copyToClipboard } from "../../../helpers";
import UpdateStatusModal from "./UpdateStatusModal";
import {
  cleanText,
  formatNaira,
  getBuyerDisplayName,
  getBuyerUser,
  getContractCode,
  getContractItems,
  getContractTotal,
  getItemImage,
  getItemName,
  getItemProductId,
  getItemQuantity,
  getItemUnitPrice,
  getPaymentStatusClasses,
  getPersonDisplayName,
  getPlaceName,
  getPublicContractStatusClasses,
  getVendorUser,
  labelPaymentStatus,
  labelPublicContractStatus,
} from "./util";

/** Only render when we have a real value — avoid walls of "—" for missing API fields. */
const Detail = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && !cleanText(value)) return null;
  return (
    <div>
      <span className="text-gray-500 text-sm">{label}</span>
      <p className="font-medium text-gray-800 mt-1 break-words">{value}</p>
    </div>
  );
};

const StatusTag = ({
  status,
  kind = "contract",
}: {
  status?: string;
  kind?: "contract" | "payment";
}) => {
  const classes =
    kind === "payment"
      ? getPaymentStatusClasses(status)
      : getPublicContractStatusClasses(status);
  const label =
    kind === "payment"
      ? labelPaymentStatus(status)
      : labelPublicContractStatus(status);

  return (
    <span
      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${classes}`}
    >
      {label}
    </span>
  );
};

const ViewPublicContractDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, detailLoading } = useAppSelector(
    (state) => state.publicContracts
  );
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchPublicContractByIdAsync(id));
    return () => {
      dispatch(resetPublicContractDetail());
    };
  }, [id, dispatch]);

  if (detailLoading && !detail) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-6">
        <button
          className="text-sm text-gray-600 mb-4"
          onClick={() => navigate(-1)}
        >
          &larr; Back
        </button>
        <WhiteTitledCard>
          <p className="text-gray-700">Public contract not found</p>
        </WhiteTitledCard>
      </div>
    );
  }

  const vendor = getVendorUser(detail);
  const buyer = getBuyerUser(detail);
  const buyerName = getBuyerDisplayName(detail);
  const vendorName = getPersonDisplayName(vendor);
  const code = getContractCode(detail);
  const items = getContractItems(detail);
  const shareUrl =
    cleanText(detail.publicUrl) ||
    cleanText(detail.shareUrl) ||
    cleanText(detail.paymentLink) ||
    "";

  const shipoutState = getPlaceName(
    detail.shipoutState || detail.shipoutStateId
  );
  const shipoutCity = getPlaceName(detail.shipoutCity || detail.shipoutCityId);
  const hasShipping =
    !!shipoutState ||
    !!shipoutCity ||
    detail.withinCity === true ||
    detail.withinCity === false ||
    !!detail.shippingDate ||
    !!detail.estimatedDeliveryDate ||
    detail.estimatedWeight != null;

  const buyerAddress =
    cleanText(buyer?.buyerAddress) ||
    [cleanText(buyer?.buyerCity), cleanText(buyer?.buyerState)]
      .filter(Boolean)
      .join(", ");

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-gray-100 min-h-screen flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button
            className="text-gray-500 hover:text-gray-800 flex items-center font-bold mb-2"
            onClick={() => navigate("/invoice-management")}
          >
            &larr; Back
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {cleanText(detail.title) || "Invoice"}
          </h1>
        </div>
        <Button
          layout="toolbar"
          size="sm"
          label="Update status"
          onClick={() => setStatusModalOpen(true)}
        />
      </div>

      <WhiteTitledCard cardTitle="Overview">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {code !== "—" && (
            <div>
              <span className="text-gray-500 text-sm">Contract code</span>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-medium text-gray-800">{code}</p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(code)}
                  className="text-gray-400 hover:text-primary"
                  aria-label="Copy contract code"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {detail.status ? (
            <div>
              <span className="text-gray-500 text-sm">Status</span>
              <div className="mt-1">
                <StatusTag status={detail.status} />
              </div>
            </div>
          ) : null}
          {detail.paymentStatus ? (
            <div>
              <span className="text-gray-500 text-sm">Payment</span>
              <div className="mt-1">
                <StatusTag status={detail.paymentStatus} kind="payment" />
              </div>
            </div>
          ) : null}
          <Detail
            label="Items amount"
            value={
              detail.itemsAmount != null
                ? formatNaira(detail.itemsAmount)
                : null
            }
          />
          <Detail
            label="Delivery cost"
            value={
              detail.deliveryCost != null
                ? formatNaira(detail.deliveryCost)
                : null
            }
          />
          <Detail
            label="Total"
            value={
              getContractTotal(detail) != null
                ? formatNaira(getContractTotal(detail))
                : null
            }
          />
          <Detail
            label="Payment option"
            value={cleanText(detail.paymentOption) || null}
          />
          <Detail
            label="Payment ref"
            value={cleanText(detail.paymentRef) || null}
          />
          <Detail
            label="Created"
            value={
              detail.createdAt
                ? moment(detail.createdAt).format("Do MMM YYYY, h:mm A")
                : null
            }
          />
          <Detail
            label="Updated"
            value={
              detail.updatedAt
                ? moment(detail.updatedAt).format("Do MMM YYYY, h:mm A")
                : null
            }
          />
          {shareUrl ? (
            <div className="md:col-span-2">
              <span className="text-gray-500 text-sm">Public link</span>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-medium text-gray-800 break-all">{shareUrl}</p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(shareUrl)}
                  className="text-gray-400 hover:text-primary"
                  aria-label="Copy public link"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : null}
        </div>
        {cleanText(detail.description) ? (
          <p className="text-sm text-gray-600 mt-4 whitespace-pre-wrap">
            {detail.description}
          </p>
        ) : null}
      </WhiteTitledCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(vendor || vendorName) && (
          <WhiteTitledCard cardTitle="Vendor">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500 text-sm">Name</span>
                <div className="mt-1">
                  {vendor?.id ? (
                    <ClickableUserName
                      user={vendor}
                      userType="VENDOR"
                      displayName={vendorName || undefined}
                    />
                  ) : (
                    <p className="font-medium text-gray-800">
                      {vendorName || "—"}
                    </p>
                  )}
                </div>
              </div>
              <Detail label="Email" value={cleanText(vendor?.email) || null} />
              <Detail
                label="Phone"
                value={
                  cleanText(vendor?.phoneNumber) ||
                  cleanText(vendor?.businessPhoneNumber) ||
                  null
                }
              />
              <Detail
                label="Business"
                value={cleanText(vendor?.businessName) || null}
              />
            </div>
          </WhiteTitledCard>
        )}

        {(buyer || buyerName !== "—") && (
          <WhiteTitledCard cardTitle="Buyer">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500 text-sm">Name</span>
                <div className="mt-1">
                  {buyer?.id ? (
                    <ClickableUserName
                      user={buyer}
                      userType="BUYER"
                      displayName={buyerName !== "—" ? buyerName : undefined}
                    />
                  ) : (
                    <p className="font-medium text-gray-800">{buyerName}</p>
                  )}
                </div>
              </div>
              <Detail label="Email" value={cleanText(buyer?.email) || null} />
              <Detail
                label="Phone"
                value={cleanText(buyer?.phoneNumber) || null}
              />
              <Detail label="Address" value={buyerAddress || null} />
            </div>
          </WhiteTitledCard>
        )}
      </div>

      {hasShipping && (
        <WhiteTitledCard cardTitle="Shipping">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Detail label="Ship-out state" value={shipoutState || null} />
            <Detail label="Ship-out city" value={shipoutCity || null} />
            <Detail
              label="Within city"
              value={
                detail.withinCity === true
                  ? "Yes"
                  : detail.withinCity === false
                    ? "No"
                    : null
              }
            />
            <Detail
              label="Shipping date"
              value={
                detail.shippingDate
                  ? moment(detail.shippingDate).format("Do MMM YYYY")
                  : null
              }
            />
            <Detail
              label="Estimated delivery"
              value={
                detail.estimatedDeliveryDate
                  ? moment(detail.estimatedDeliveryDate).format("Do MMM YYYY")
                  : null
              }
            />
            <Detail
              label="Estimated weight"
              value={
                detail.estimatedWeight != null
                  ? `${detail.estimatedWeight} kg`
                  : null
              }
            />
          </div>
        </WhiteTitledCard>
      )}

      <WhiteTitledCard cardTitle={`Items (${items.length})`}>
        {items.length === 0 ? (
          <p className="text-sm text-gray-500">No items on this invoice</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="bg-gray-100 text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2">Item</th>
                  <th className="px-4 py-2">Qty</th>
                  <th className="px-4 py-2">Price</th>
                  <th className="px-4 py-2">Line total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const qtyRaw = getItemQuantity(item);
                  const qty = Number(qtyRaw) || 0;
                  const unit = getItemUnitPrice(item);
                  const line =
                    item.totalCost != null
                      ? Number(item.totalCost)
                      : unit != null
                        ? qty * unit
                        : null;
                  const name = getItemName(item);
                  const productId = getItemProductId(item);
                  const image = getItemImage(item);
                  return (
                    <tr key={item.id ?? index} className="border-t">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {image ? (
                            <img
                              src={image}
                              alt=""
                              className="w-10 h-10 rounded object-cover shrink-0 bg-gray-100"
                            />
                          ) : null}
                          {productId ? (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/products/details/${productId}`)
                              }
                              className="text-left text-purple-600 hover:text-purple-800 hover:underline font-medium truncate"
                              title="View product"
                            >
                              {name}
                            </button>
                          ) : (
                            <span className="font-medium text-gray-800 truncate">
                              {name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {qtyRaw != null ? qtyRaw : "—"}
                      </td>
                      <td className="px-4 py-3">{formatNaira(unit)}</td>
                      <td className="px-4 py-3">{formatNaira(line)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </WhiteTitledCard>

      {statusModalOpen && (
        <UpdateStatusModal
          key={`${detail.id}-${detail.status}`}
          open={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          contract={detail}
        />
      )}
    </div>
  );
};

export default ViewPublicContractDetails;
